import { Tooltip as RT } from 'radix-ui';
import { cn } from '@/lib/cn';
import { useState, type MouseEvent, type PointerEvent, type ReactNode } from 'react';

export const TooltipProvider = RT.Provider;

/**
 * Wrap any trigger; `label` may include a <Kbd/>. Delay is short because these annotate icon-only controls.
 * Radix opens a tooltip on hover and focus only, never on touch; `tap` also toggles it on a click or tap, for a hint
 * a phone should reach too. A tap elsewhere closes it. `wide` is for a sentence or two explaining a section: it wraps
 * at a readable measure instead of running on as one line.
 */
export function Tooltip({ label, children, side = 'bottom', tap = false, wide = false }: { label: ReactNode; children: ReactNode; side?: 'top' | 'bottom' | 'left' | 'right'; tap?: boolean; wide?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <RT.Root delayDuration={300} {...(tap && { open, onOpenChange: setOpen })}>
      {/* Radix closes the tooltip on the trigger's pointerdown and click, after any handler given here, unless that
          handler prevents the default; in tap mode it does, so the tap alone decides. */}
      <RT.Trigger asChild {...(tap && {
        onPointerDown: (e: PointerEvent) => e.preventDefault(),
        onClick: (e: MouseEvent) => { e.preventDefault(); setOpen((o) => !o); },
      })}>{children}</RT.Trigger>
      <RT.Portal>
        <RT.Content side={side} sideOffset={6}
          className={cn('z-50 flex items-center gap-2 rounded-input bg-toast-bg text-toast-fg text-[12px] font-bold px-[10px] h-7 shadow-float select-none',
            wide && 'block h-auto max-w-[260px] py-[8px] leading-[1.45] font-semibold')}>
          {label}
        </RT.Content>
      </RT.Portal>
    </RT.Root>
  );
}
